import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { OnyxCallbackOption, OnyxChip } from '@onyx/angular';
import {
  OnyxChipsComponent,
  OnyxDropdownDirective,
  OnyxIconButtonComponent,
  OnyxIconComponent,
  OnyxModalComponent,
  OnyxTooltipDirective,
} from '@onyx/angular';
import { isString } from 'lodash';
import { catchError, EMPTY, Subject, switchMap, tap } from 'rxjs';
import { ModalNoteComponent } from '../../../common/components/modals/modal-note/modal-note.component';
import { ModalsStore } from '../../../common/stores/modals.store';
import { DelegationStatus } from '../common/enums/delegation-status';
import { DelegationsRoute } from '../common/enums/delegations-route';
import {
  DELEGATION_HEADER_ACTIONS,
  DelegationHelper,
} from '../common/helpers/delegation.helper';
import type {
  Delegation,
  SimplifiedDelegation,
} from '../common/interfaces/delegation';
import { DelegationsService } from '../common/services/delegations.service';
import { DelegationActionModalMode } from '../delegation-action-modal/delegation-action-modal.component';
import { DelegationModalHeaderDescriptionComponent } from './delegation-modal-header-description/delegation-modal-header-description.component';
import { DelegationModalSummaryComponent } from './delegation-modal-sections/delegation-modal-summary/delegation-modal-summary.component';

export type DelegationModalData = Delegation | SimplifiedDelegation | string;

enum DelegationModalSection {
  SUMMARY,
  NOTE,
}

@Component({
  selector: 'app-delegation-modal',
  imports: [
    OnyxModalComponent,
    OnyxIconButtonComponent,
    OnyxIconComponent,
    OnyxChipsComponent,
    OnyxDropdownDirective,
    OnyxTooltipDirective,
    ModalNoteComponent,
    DelegationModalSummaryComponent,
    DelegationModalHeaderDescriptionComponent,
  ],
  templateUrl: './delegation-modal.component.html',
  styleUrl: './delegation-modal.component.scss',
})
export class DelegationModalComponent {
  protected readonly data = inject<DelegationModalData>(DIALOG_DATA);
  protected readonly dialogRef = inject(DialogRef<void>);
  private readonly delegationService = inject(DelegationsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly delegationHelper = inject(DelegationHelper);
  private readonly modalsStore = inject(ModalsStore);

  protected readonly SECTIONS: OnyxChip<DelegationModalSection>[] = [
    { name: 'labels.summary', value: DelegationModalSection.SUMMARY },
    { name: 'labels.note', value: DelegationModalSection.NOTE },
  ];

  protected readonly DelegationRoute = DelegationsRoute;

  private readonly uuid: string;

  protected DelegationModalSection = DelegationModalSection;

  protected delegation = signal<Delegation | null>(null);
  protected section = this.modalsStore.getActiveSection(
    DelegationModalComponent.name,
    () => this.SECTIONS,
  );
  protected loading = signal(false);
  protected error = signal(false);

  protected close$ = new Subject<void>();
  protected delegation$ = new Subject<void>();

  protected actionButton = computed<OnyxCallbackOption | null>(() => {
    const delegation = this.delegation();
    if (!delegation) return null;

    const actionButtons: Partial<Record<DelegationStatus, OnyxCallbackOption>> =
      {
        [DelegationStatus.PLANNED]: {
          name: 'labels.activate',
          value: () =>
            this.openDelegationActionModal(
              DelegationActionModalMode.ACTIVATE,
              this.delegation()!,
            ),
        },
        [DelegationStatus.ACTIVE]: {
          name: 'labels.finish',
          disabled: !!delegation.relatedFleetSwap,
          tooltip: delegation.relatedFleetSwap
            ? 'labels.disabledUntilSwapCompleted'
            : undefined,
          value: () =>
            this.openDelegationActionModal(
              DelegationActionModalMode.FINISH,
              this.delegation()!,
            ),
        },
        [DelegationStatus.UNSETTLED]: {
          name: 'labels.settle',
          value: () =>
            this.openDelegationActionModal(
              DelegationActionModalMode.SETTLE,
              this.delegation()!,
            ),
        },
      };

    return actionButtons[delegation.status.value] ?? null;
  });
  protected options = computed(() =>
    this.delegation()
      ? this.delegationHelper.getOptions(
          this.delegation()!,
          { close$: this.close$ },
          DELEGATION_HEADER_ACTIONS,
        )
      : null,
  );
  protected hasEdit = computed(() =>
    this.delegation() ? DelegationHelper.hasEdit(this.delegation()!) : false,
  );

  constructor() {
    this.uuid = isString(this.data) ? this.data : this.data.uuid;

    this.delegation$
      .pipe(
        tap(() => this.loading.set(true)),
        switchMap(() =>
          this.delegationService.getDelegation(this.uuid).pipe(
            catchError(() => {
              this.delegation.set(null);
              this.loading.set(false);
              this.error.set(true);

              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((delegation) => {
        this.delegation.set(delegation);
        this.loading.set(false);
        this.error.set(false);
      });

    if (isString(this.data) || !DelegationHelper.isDelegationType(this.data)) {
      this.delegation$.next();
    } else {
      this.delegation.set(this.data);
    }

    this.delegationService.reload$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.delegation$.next());
  }

  protected edit(): void {
    this.delegationHelper.edit(this.uuid, { close$: this.close$ });
  }

  protected share(): void {
    const delegation = this.delegation();
    if (delegation) this.delegationHelper.share(delegation);
  }

  protected showOnMap(): void {
    const delegation = this.delegation();
    if (delegation) {
      this.delegationHelper.showOnMap(delegation, { close$: this.close$ });
    }
  }

  protected showOnGantt(): void {
    const delegation = this.delegation();
    if (delegation) {
      this.delegationHelper.showOnGantt(delegation, { close$: this.close$ });
    }
  }

  private openDelegationActionModal(
    mode: DelegationActionModalMode,
    delegation: Delegation,
  ): void {
    this.delegationHelper.openActionModal(mode, delegation);
  }
}
